/**
 * R110.93 — Investor journey tracker.
 *
 * Client-side session log written to localStorage under a single key so we
 * can attach a `timestamps_and_activity` object to every lead we POST to
 * the backend (see franchise-leads/bulk and /leads/capture). All timestamps
 * are ISO 8601 UTC; durations are floats in seconds.
 *
 * Everything is best-effort — if localStorage is unavailable (Safari
 * Private, cross-origin iframes) we degrade to an in-memory copy for the
 * current tab so the payload still travels with a lead submission.
 */

const KEY = "vk_investor_journey_v1";
const now = () => new Date().toISOString();

// In-memory mirror so we survive localStorage failures.
let mem = null;

// R110.96 — Fire-and-forget consent+event capture to the backend so the
// admin lead pool sees every touchpoint the moment it happens. We import
// lazily to avoid circular deps with the API client bootstrapping.
async function _fireCapture(event, extra) {
  try {
    const mod = await import("@/lib/api");
    const api = mod.default;
    // Skip if we don't have a bearer token (guest browse — capture kicks
    // in once the user logs in and returns).
    let hasToken = false;
    try { hasToken = !!window.localStorage.getItem("venturekhoj_token"); } catch (_) {}
    if (!hasToken) return;
    await api.post("/investor-leads/capture", {
      event,
      ...(extra || {}),
      // Send the full journey snapshot on the FIRST event of a session so
      // the backend can hydrate the pool row with historical browse data
      // that pre-dates the auth boundary.
      timestamps_and_activity: getJourneyPayload(),
    });
  } catch (_e) {
    // Never let capture failures block a UI interaction — the journey is
    // still in localStorage and will attach to the next /franchise-leads
    // POST as before.
  }
}

function safeParse(raw) {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch (_e) {
    return null;
  }
}

function readState() {
  if (mem) return mem;
  try {
    const raw = window.localStorage.getItem(KEY);
    const parsed = safeParse(raw);
    if (parsed) { mem = parsed; return parsed; }
  } catch (_e) { /* private mode */ }
  return null;
}

function writeState(state) {
  mem = state;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(state));
  } catch (_e) { /* private mode — mem is enough */ }
}

/**
 * Initialise the journey record. Idempotent — safe to call on every route.
 * Sets `profile_created_at` on the first call and never touches it again.
 */
export function initTracker() {
  let state = readState();
  if (!state) {
    state = {
      profile_created_at: now(),
      ideas_explored: [],
      // R110.99 — SEO enhancement: passively track category-level browsing
      // + compare interactions so the admin lead pool can surface cross-
      // category intent. Both arrays are deduped by slug (see below).
      explored_categories: [],
      compared_ideas: [],
      primary_feasibility_report: null,
      matched_brands_viewed: [],
      express_interest_clicked_at: null,
      // Ephemeral — cleared on submit. Used to compute report time-spent.
      _report_timer: null,
    };
    writeState(state);
  } else {
    // Back-fill arrays for pre-R110.99 clients so getJourneyPayload never
    // throws on missing keys.
    let touched = false;
    if (!Array.isArray(state.explored_categories)) { state.explored_categories = []; touched = true; }
    if (!Array.isArray(state.compared_ideas))      { state.compared_ideas      = []; touched = true; }
    if (touched) writeState(state);
  }
  return state;
}

function ensure() {
  return readState() || initTracker();
}

// ---------------------------------------------------------------------------
// Idea + teaser view logs (dedupe by name so refreshes don't inflate counts)
// ---------------------------------------------------------------------------

export function trackIdeaViewed(idea_name, category_id = null) {
  if (!idea_name) return;
  const state = ensure();
  const key = String(idea_name).trim().toLowerCase();
  const already = state.ideas_explored.some(
    (i) => String(i.idea_name || "").trim().toLowerCase() === key
  );
  if (already) return;
  state.ideas_explored.push({
    idea_name: String(idea_name),
    category_id: category_id || null,
    viewed_at: now(),
  });
  // Cap to keep the payload lean.
  if (state.ideas_explored.length > 40) state.ideas_explored.shift();
  writeState(state);
  // R110.96 — Fire consent-capture (DPDP: exploration = consent).
  _fireCapture("idea_view", {
    action_summary: `Explored ${idea_name}`,
    idea: { idea_name: String(idea_name), category_id: category_id || null },
  });
}

export function trackTeaserViewed(brand_name, franchise_id = null, idea_name = null) {
  if (!brand_name && !franchise_id) return;
  const state = ensure();
  const key = franchise_id || String(brand_name || "").toLowerCase();
  const already = state.matched_brands_viewed.some(
    (t) => (t.franchise_id || String(t.brand_name || "").toLowerCase()) === key
  );
  if (already) return;
  state.matched_brands_viewed.push({
    brand_name:      brand_name || null,
    franchise_id:    franchise_id || null,
    idea_name:       idea_name || null,
    teaser_viewed_at: now(),
  });
  if (state.matched_brands_viewed.length > 60) state.matched_brands_viewed.shift();
  writeState(state);
  _fireCapture("teaser_view", {
    action_summary: `Viewed teaser for ${brand_name || franchise_id || "brand"}`,
    brand: { brand_name: brand_name || null, franchise_id: franchise_id || null, idea_name: idea_name || null },
  });
}

// ---------------------------------------------------------------------------
// Report timer + primary feasibility summary
// ---------------------------------------------------------------------------

export function startReportTimer({ report_id, idea_name, feasibility_score = null, generated_at = null }) {
  if (!report_id) return;
  const state = ensure();
  state._report_timer = {
    report_id,
    idea_name: idea_name || null,
    feasibility_score: feasibility_score != null ? Number(feasibility_score) : null,
    generated_at: generated_at || now(),
    started_at: now(),
  };
  writeState(state);
}

export function stopReportTimer(report_id) {
  const state = ensure();
  const t = state._report_timer;
  if (!t || (report_id && t.report_id !== report_id)) return;
  const startedMs = Date.parse(t.started_at);
  const elapsed = Math.max(0, (Date.now() - (startedMs || Date.now())) / 1000);
  // Only overwrite the primary report if this is the first one OR the user
  // spent longer on this one (higher signal).
  const prev = state.primary_feasibility_report;
  const newSummary = {
    report_id: t.report_id,
    idea_name: t.idea_name,
    feasibility_score: t.feasibility_score,
    generated_at: t.generated_at,
    report_time_spent_seconds: Math.round(elapsed * 10) / 10,
  };
  if (!prev || Number(newSummary.report_time_spent_seconds) > Number(prev.report_time_spent_seconds || 0)) {
    state.primary_feasibility_report = newSummary;
  }
  state._report_timer = null;
  writeState(state);
  // R110.96 — Ship the report view/time-spent to the pool row.
  _fireCapture("report_view", {
    action_summary: `Generated feasibility report for ${newSummary.idea_name || "an idea"}`,
    report: newSummary,
  });
}

export function trackCompare(brand_names) {
  if (!Array.isArray(brand_names) || brand_names.length < 2) return;
  // R110.99 — Persist the compare event in-journey so the admin/franchisor
  // can see cross-idea comparison intent, not just brand comparisons.
  const state = ensure();
  const compact = brand_names.slice(0, 6).map((b) => String(b).trim()).filter(Boolean);
  if (compact.length >= 2) {
    state.compared_ideas.push({ items: compact, compared_at: now() });
    if (state.compared_ideas.length > 30) state.compared_ideas.shift();
    writeState(state);
  }
  _fireCapture("compare", {
    action_summary: `Compared ${brand_names.length} brands`,
    compared_brand_names: compact,
  });
}

// R110.99 — SEO: passively record which top-level category the user is
// currently browsing. Called from LocationLanding + SeoLanding hooks
// (industry/venture-type/investment-band pages) on mount. Silently no-ops
// when category_id is missing — no UI blocking, no network call unless
// this category hasn't been seen this session.
export function trackCategoryExplored(category_id, extra = {}) {
  if (!category_id) return;
  const state = ensure();
  const key = String(category_id).trim().toLowerCase();
  const already = state.explored_categories.some(
    (c) => String(c.category_id || "").trim().toLowerCase() === key
  );
  if (already) return;
  state.explored_categories.push({
    category_id: String(category_id),
    category_name: extra.category_name || null,
    page_type: extra.page_type || null,     // "city" | "business_idea" | "hybrid"
    city_name: extra.city_name || null,
    viewed_at: now(),
  });
  if (state.explored_categories.length > 30) state.explored_categories.shift();
  writeState(state);
  _fireCapture("category_view", {
    action_summary: `Browsed ${extra.category_name || category_id} category`,
    category: {
      category_id: String(category_id),
      category_name: extra.category_name || null,
      page_type: extra.page_type || null,
      city_name: extra.city_name || null,
    },
  });
}

// ---------------------------------------------------------------------------
// Express-interest click + payload assembly
// ---------------------------------------------------------------------------

export function markExpressInterest() {
  const state = ensure();
  state.express_interest_clicked_at = now();
  writeState(state);
  _fireCapture("express_interest", { action_summary: "Clicked Connect / Request callback" });
}

/**
 * Compile the payload attached to /franchise-leads/bulk and /leads/capture.
 * Strips the internal `_report_timer` scratchpad.
 */
export function getJourneyPayload() {
  const state = ensure();
  // Clone + drop private keys before shipping.
  const clone = JSON.parse(JSON.stringify(state));
  delete clone._report_timer;
  return clone;
}

/** Clear the journey record — call after a successful lead submission if
 *  you want a fresh session, or leave it in place to accumulate across
 *  multiple lead submissions in the same session. */
export function resetTracker() {
  mem = null;
  try { window.localStorage.removeItem(KEY); } catch (_e) { /* noop */ }
}
